import React, { forwardRef } from "react";
import { Box, Typography, Grid } from "@mui/material";
import {
  getPodcastImageUrl,
  getProgramImageUrl,
  getS3Url,
  resolveProgramCoverUrl,
} from "../../utils/imageUtils";
import { RankingCardHeader } from "./RankingCardHeader";
import {
  getRankingDimensionFillForParam,
} from "./RankingDimensionStepper";
import { LivestreamRankingTable } from "./LivestreamRankingTable";
import { formatMetric } from "./rankingCardMetrics";
import type { RankingParam } from "./rankingCardCopy";
import {
  RANKING_ASSET_HEIGHT_PX,
  RANKING_ASSET_ROW_BORDER_RADIUS_PX,
  RANKING_ASSET_ROW_GAP_PX,
  RANKING_ASSET_ROW_HEIGHT_PX,
  RANKING_ASSET_ROW_INNER_GAP_PX,
  RANKING_ASSET_ROW_PADDING_X_PX,
  RANKING_ASSET_ROW_PADDING_Y_PX,
  RANKING_ASSET_WIDTH_PX,
} from "./rankingAssetDimensions";
import { ProgramThumbWithPodcastBadge } from "./ProgramThumbWithPodcastBadge";
import { RANKING_CAPTURE_FULL_TEXT_ATTR } from "./rankingTableLayout";

/** Background image for the ranking card (Figma design). Served from public so it works without Figma running. */
const RANKING_CARD_BG_URL = "/ranking-bg.png";

/** SF Symbol–style peak indicator for livestream metrics (public/images). */
const PEAK_UP_TRIANGLE_SVG_URL = "/images/arrowtriangle.up.fill.svg";

/** Footer explainer text: max width (ranking card–relative). */
const FOOTER_EXPLAINER_MAX_WIDTH_PX = RANKING_ASSET_WIDTH_PX / 1.8;

export interface ProgramRow {
  _id: string;
  name: string;
  imageS3Key?: string;
  imageUpdatedAt?: string;
  updatedAt?: string;
  podcast: {
    _id: string;
    title: string;
    imageS3Key?: string;
    imageUrl?: string;
    imageUpdatedAt?: string | Date;
  };
  avgViews: number;
  /** Mean views at ~7 days after publish per episode (programs analysis). */
  avgViewsDay7?: number;
  avgInteractions?: number;
  /** Mean likes+comments at ~7 days after publish per episode. */
  avgInteractionsDay7?: number;
  /** Sum of likes+comments at ~7 days after publish across episodes. */
  totalInteractionsDay7?: number;
  uniquePaidBrandsCount?: number;
  /** Current rank in this ranking (1–10). */
  rankingPosition?: number;
  /** Rank in previous period; null = new entry. */
  positionLastWeek?: number | null;
}

/** Channel (podcast) row for channels-by-social ranking. */
export interface ChannelRow {
  _id: string;
  name: string;
  imageS3Key?: string;
  imageUrl?: string;
  /** Program thumbnail cache-bust (when imageS3Key is from top program by social views). */
  imageUpdatedAt?: string | Date;
  updatedAt?: string | Date;
  /** Channel image for program-group livestream rows (badge overlay on program cover). */
  channelImageS3Key?: string;
  totalViews?: number;
  contentCount?: number;
  /** Explorer livestream monitor: weighted avg concurrent viewers for the period. */
  avgLiveViewers?: number;
  /** Explorer livestream monitor: highest peak viewers across all streams in the period. */
  peakLiveViewers?: number;
  /** Explorer livestream: avg unique chat users per stream (aggregated in API metrics). */
  avgUniqueChatUsers?: number;
  /** Explorer livestream: number of livestreams in the ranking period (`episodeCount` from API). */
  livestreamCount?: number;
}

/** Brand row for brands-by-views ranking. */
export interface BrandRow {
  _id: string;
  name: string;
  imageS3Key?: string;
  category?: string;
  totalViews?: number;
}

export type RankingItem = ProgramRow | ChannelRow | BrandRow;

export interface RadarPodscopeRankingCardProps {
  /** Top 10 programs (already sorted). Use for views, interactions, programs-brands. */
  programs?: ProgramRow[];
  /** Top 10 channels (podcasts). Use for channels-social. */
  channels?: ChannelRow[];
  /** Top 10 brands (legacy ranking asset). */
  brands?: BrandRow[];
  /** Label for the period pill (e.g. "Semana del 1 al 7 de Marzo"). */
  rankingPeriodLabel: string;
  /** Subheader under the main title (e.g. "Programas por views promedio"). */
  subheader: string;
  /** Footer disclaimer / methodology text (left side). */
  footerExplainer: string;
  /** Optional right-side footer text. Default: "Detalles en radar.podscope.co" */
  footerRight?: string;
  /** 'page' = responsive (aspect ratio); 'screenshot' = fixed asset size for Puppeteer. */
  variant?: "page" | "screenshot";
  /** When showing programs: which metric to display under the title (position + metric replace podcast line). */
  programRankingMetric?: "views" | "interactions" | "interactions-total" | "programs-brands";
  /** When showing channels: social short-form views vs Explorer livestream avg viewers. */
  channelsMetric?: "social-views" | "livestream-avg";
  /** Which ranking dimension this asset is for — footer explainer uses dimension fill when set. */
  rankingParam?: RankingParam;
  /** When true, the period label pill is not rendered. */
  hidePeriodPill?: boolean;
  /** Livestream screenshot table: first column label (`Canal` vs `Programa`). */
  livestreamTableNameColumnHeader?: string;
}

const rowBoxSx = {
  display: "flex",
  alignItems: "center",
  gap: `${RANKING_ASSET_ROW_INNER_GAP_PX}px`,
  px: `${RANKING_ASSET_ROW_PADDING_X_PX}px`,
  py: `${RANKING_ASSET_ROW_PADDING_Y_PX}px`,
  height: RANKING_ASSET_ROW_HEIGHT_PX,
  borderRadius: `${RANKING_ASSET_ROW_BORDER_RADIUS_PX}px`,
  bgcolor: "rgba(33,35,45,0.7)",
} as const;

const thumbSx = {
  width: 100,
  height: 100,
  flexShrink: 0,
  borderRadius: "20px",
  overflow: "hidden" as const,
  bgcolor: "rgba(33,35,45,0.7)",
  position: "relative" as const,
};

/** Program thumb allows podcast badge to overflow outside the cover. */
const programThumbSx = {
  ...thumbSx,
  overflow: "visible" as const,
  bgcolor: "transparent",
};

/** Clips the program cover to the rounded thumb; badge sits outside this. */
const thumbMediaSx = {
  position: "absolute" as const,
  inset: 0,
  borderRadius: "20px",
  overflow: "hidden" as const,
  bgcolor: "rgba(33,35,45,0.7)",
};

/**
 * lineHeight:1 keeps lines tight; paddingBottom extends the clip box below line 2
 * so descenders (g, y, p...) aren't cut without adding space between the two lines.
 */
const rankingTitleSx = {
  fontFamily: '"DM Serif Text", serif',
  fontSize: 32,
  lineHeight: 1.0,
  paddingBottom: "4px",
  overflow: "hidden",
  color: "#fff",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  textOverflow: "ellipsis",
  overflowWrap: "anywhere",
} as const;

/** PNG capture: show full program/channel names (no line-clamp or ellipsis). */
const rankingTitleCaptureSx = {
  fontFamily: '"DM Serif Text", serif',
  fontSize: 32,
  lineHeight: 1.05,
  paddingBottom: "4px",
  color: "#fff",
  overflow: "visible",
  overflowWrap: "anywhere",
  whiteSpace: "normal",
} as const;

const rankingRowCaptureShellSx = {
  height: "auto",
  minHeight: RANKING_ASSET_ROW_HEIGHT_PX,
} as const;

/** Shared container for Figma ranking badges (8788-2035, 8788-2075, 8788-2083, 8788-2091) */
const rankingBadgeRowSx = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  minHeight: "26px",
  flexWrap: "wrap" as const,
} as const;

/** Pill: 40px width, 12px radius, 6px horizontal 3px vertical padding */
const rankingPillSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  px: "6px",
  borderRadius: "12px",
  width: 40,
  height: "22px",
  flexShrink: 0,
} as const;

const rankingMetricSx = {
  fontFamily: '"DM Serif Text", serif',
  fontSize: 26,
  lineHeight: "26px",
  color: "#fff",
  flex: "1 0 0",
  minWidth: 0,
} as const;

/** Livestream channel row: avg + peak; slightly smaller than program metrics. */
const livestreamChannelMetricRowSx = {
  fontFamily: '"DM Serif Text", serif',
  fontSize: 26,
  lineHeight: "26px",
  color: "#fff",
  flex: "1 0 0",
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  gap: "12px",
  flexWrap: "wrap",
} as const;

const rankingPillNumberSx = {
  fontFamily: '"DM Serif Text", serif',
  fontSize: 20,
  lineHeight: "20px",
  color: "#fff",
} as const;

/** Inline SVGs from Figma (arrow up/down, equals) — work without localhost assets */
const ArrowUpSvg = () => (
  <svg
    width="11"
    height="12"
    viewBox="0 0 11 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: "block", marginBottom: "-1px", marginLeft: "-1px" }}
  >
    <path
      d="M5.5 2v8M2 5.5l3.5-3.5L9 5.5"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const ArrowDownSvg = () => (
  <svg
    width="11"
    height="12"
    viewBox="0 0 11 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: "block", marginBottom: "-1px", marginLeft: "-1px" }}
  >
    <path
      d="M5.5 10V2M2 6.5l3.5 3.5L9 6.5"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const EqualsSvg = () => (
  <svg
    width="12"
    height="8"
    viewBox="0 0 12 8"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: "block" }}
  >
    <path
      d="M1 2h10M1 6h10"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

/** Figma 8788-2091: position NEW — teal pill with "N", then metric 32px */
function RankingNewBadge({
  metricLabel,
}: {
  rank: number;
  metricLabel: string;
}) {
  return (
    <Box sx={rankingBadgeRowSx}>
      <Box sx={{ ...rankingPillSx, bgcolor: "#00c49a" }}>
        <Typography component="span" sx={rankingPillNumberSx}>
          N
        </Typography>
      </Box>
      <Typography component="span" sx={rankingMetricSx}>
        {metricLabel}
      </Typography>
    </Box>
  );
}

/** Figma 8788-2035: position UP — teal pill with arrow + change amount, then metric 32px */
function RankingUpBadge({
  change,
  metricLabel,
}: {
  change: number;
  metricLabel: string;
}) {
  return (
    <Box sx={rankingBadgeRowSx}>
      <Box sx={{ ...rankingPillSx, bgcolor: "#00c49a", gap: "1px" }}>
        <ArrowUpSvg />
        <Typography component="span" sx={rankingPillNumberSx}>
          {change}
        </Typography>
      </Box>
      <Typography component="span" sx={rankingMetricSx}>
        {metricLabel}
      </Typography>
    </Box>
  );
}

/** Figma 8788-2075: position DOWN — red pill with arrow + change amount, then metric 32px */
function RankingDownBadge({
  change,
  metricLabel,
}: {
  change: number;
  metricLabel: string;
}) {
  return (
    <Box sx={rankingBadgeRowSx}>
      <Box sx={{ ...rankingPillSx, bgcolor: "#ff6b6b", gap: "1px", px: "5px" }}>
        <ArrowDownSvg />
        <Typography component="span" sx={rankingPillNumberSx}>
          {change}
        </Typography>
      </Box>
      <Typography component="span" sx={rankingMetricSx}>
        {metricLabel}
      </Typography>
    </Box>
  );
}

/** Figma 8788-2083: position EQUAL — gray pill with equals icon only, then metric 32px */
function RankingEqualBadge({
  metricLabel,
}: {
  rank: number;
  metricLabel: string;
}) {
  return (
    <Box sx={rankingBadgeRowSx}>
      <Box sx={{ ...rankingPillSx, bgcolor: "#c4bfbf" }}>
        <EqualsSvg />
      </Box>
      <Typography component="span" sx={rankingMetricSx}>
        {metricLabel}
      </Typography>
    </Box>
  );
}

function PositionAndMetric({
  rank,
  positionLastWeek,
  metricValue,
  metricType,
  hidePositionChange,
}: {
  rank: number;
  positionLastWeek: number | null | undefined;
  metricValue: number;
  metricType?: "views" | "interactions" | "interactions-total" | "programs-brands";
  hidePositionChange?: boolean;
}) {
  const metricLabel = metricType
    ? formatMetric(
        metricValue,
        metricType === "interactions-total" ? "interactions" : metricType,
      )
    : null;
  if (!metricLabel) {
    return null;
  }

  // Metric only: no up/down/new/equal pills (e.g. brands ranking, screenshot assets)
  if (metricType === "programs-brands" || hidePositionChange) {
    return (
      <Box sx={rankingBadgeRowSx}>
        <Typography component="span" sx={rankingMetricSx}>
          {metricLabel}
        </Typography>
      </Box>
    );
  }

  const isNew = positionLastWeek === null || positionLastWeek === undefined;
  const change = !isNew ? positionLastWeek - rank : 0; // positive = moved up

  if (isNew) {
    return <RankingNewBadge rank={rank} metricLabel={metricLabel} />;
  }
  if (change > 0) {
    return <RankingUpBadge change={change} metricLabel={metricLabel} />;
  }
  if (change < 0) {
    return (
      <RankingDownBadge change={Math.abs(change)} metricLabel={metricLabel} />
    );
  }
  return <RankingEqualBadge rank={rank} metricLabel={metricLabel} />;
}

function ProgramCard({
  program,
  rank,
  metricType,
  hidePositionChange,
  expandTitle,
}: {
  program: ProgramRow;
  rank: number;
  metricType?: "views" | "interactions" | "interactions-total" | "programs-brands";
  hidePositionChange?: boolean;
  expandTitle?: boolean;
}) {
  const currentRank = program.rankingPosition ?? rank;
  const metricValue =
    metricType === "views"
      ? (program.avgViewsDay7 ?? program.avgViews)
      : metricType === "interactions"
        ? (program.avgInteractionsDay7 ?? program.avgInteractions ?? 0)
        : metricType === "interactions-total"
          ? (program.totalInteractionsDay7 ?? 0)
        : metricType === "programs-brands"
          ? (program.uniquePaidBrandsCount ?? 0)
          : 0;
  const showPositionAndMetric =
    metricType && (metricType === "programs-brands" || metricValue > 0);
  const coverUrl = resolveProgramCoverUrl({
    program,
    podcast: program.podcast,
  });
  const podcastImageUrl = program.podcast
    ? getPodcastImageUrl(program.podcast)
    : "";
  // Badge when cover is the program’s own image and a podcast image exists.
  const showPodcastBadge = Boolean(program.imageS3Key && podcastImageUrl);

  return (
    <Box sx={expandTitle ? { ...rowBoxSx, ...rankingRowCaptureShellSx } : rowBoxSx}>
      <Typography
        sx={{
          fontFamily: '"DM Serif Text", serif',
          fontSize: 32,
          minWidth: 38,
          color: "#fff",
        }}
      >
        {rank}.
      </Typography>
      <Box sx={programThumbSx}>
        {coverUrl && showPodcastBadge ? (
          <ProgramThumbWithPodcastBadge
            coverUrl={coverUrl}
            podcastImageUrl={podcastImageUrl}
            maskId={`podcast-badge-cutout-${program._id}`}
          />
        ) : (
          <Box sx={thumbMediaSx}>
            {coverUrl ? (
              <Box
                component="img"
                src={coverUrl}
                alt=""
                sx={{
                  width: "102%",
                  height: "102%",
                  objectFit: "cover",
                  margin: "-1%",
                  display: "block",
                }}
              />
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontFamily: '"DM Serif Text", serif',
                  fontSize: 32,
                  lineHeight: "32px",
                }}
              >
                {program.name.charAt(0).toUpperCase()}
              </Box>
            )}
          </Box>
        )}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          component="div"
          {...{ [RANKING_CAPTURE_FULL_TEXT_ATTR]: expandTitle ? "" : undefined }}
          // Emotion drops WebkitBoxOrient from `sx`; keep it inline for line-clamp.
          style={
            expandTitle
              ? undefined
              : ({ WebkitBoxOrient: "vertical" } as React.CSSProperties)
          }
          sx={expandTitle ? rankingTitleCaptureSx : rankingTitleSx}
        >
          {program.name}
        </Typography>
        <PositionAndMetric
          rank={currentRank}
          positionLastWeek={program.positionLastWeek}
          metricValue={metricValue}
          metricType={metricType}
          hidePositionChange={hidePositionChange}
        />
      </Box>
    </Box>
  );
}

function ChannelCard({
  channel,
  rank,
  channelsMetric = "social-views",
  expandTitle,
}: {
  channel: ChannelRow;
  rank: number;
  channelsMetric?: "social-views" | "livestream-avg";
  expandTitle?: boolean;
}) {
  const imgUrl = channel.imageS3Key
    ? getProgramImageUrl({
        imageS3Key: channel.imageS3Key,
        imageUpdatedAt: channel.imageUpdatedAt,
      })
    : (channel.imageUrl ?? "");
  const totalViews = channel.totalViews ?? 0;
  const avgLive = channel.avgLiveViewers ?? 0;
  const peakLive = channel.peakLiveViewers ?? 0;
  const isLivestream = channelsMetric === "livestream-avg";
  const showMetric = isLivestream ? avgLive > 0 : totalViews > 0;
  const viewsLabel =
    !isLivestream && totalViews > 0
      ? formatMetric(totalViews, "channels-social")
      : null;
  return (
    <Box sx={expandTitle ? { ...rowBoxSx, ...rankingRowCaptureShellSx } : rowBoxSx}>
      <Typography
        sx={{
          fontFamily: '"DM Serif Text", serif',
          fontSize: 32,
          minWidth: 38,
          color: "#fff",
        }}
      >
        {rank}.
      </Typography>
      <Box sx={thumbSx}>
        {imgUrl ? (
          <Box
            component="img"
            src={imgUrl}
            alt=""
            sx={{
              width: "102%",
              height: "102%",
              objectFit: "cover",
              margin: "-1%",
              display: "block",
            }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontFamily: '"DM Serif Text", serif',
              fontSize: 32,
              lineHeight: "32px",
            }}
          >
            {channel.name.charAt(0).toUpperCase()}
          </Box>
        )}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          component="div"
          {...{ [RANKING_CAPTURE_FULL_TEXT_ATTR]: expandTitle ? "" : undefined }}
          sx={expandTitle ? rankingTitleCaptureSx : rankingTitleSx}
        >
          {channel.name}
        </Typography>
        {isLivestream && showMetric ? (
          <Box sx={livestreamChannelMetricRowSx}>
            <Box component="span" sx={{ whiteSpace: "nowrap" }}>
              ~{"\u2009"}
              {formatMetric(avgLive, "livestream-avg")}
            </Box>
            {peakLive > 0 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "3px" }}>
                <Box
                  component="img"
                  src={PEAK_UP_TRIANGLE_SVG_URL}
                  alt=""
                  sx={{
                    width: 12,
                    height: 12,
                    flexShrink: 0,
                    display: "block",
                  }}
                />
                <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                  {formatMetric(peakLive, "livestream-avg")}
                </Box>
              </Box>
            )}
          </Box>
        ) : showMetric && viewsLabel ? (
          <Box sx={livestreamChannelMetricRowSx}>
            <Box component="span" sx={{ whiteSpace: "nowrap" }}>
              {viewsLabel}
            </Box>
          </Box>
        ) : (
          <Typography
            sx={{
              fontFamily: '"DM Serif Text", serif',
              fontSize: 20,
              lineHeight: "20px",
              color: "rgba(255,255,255,0.9)",
            }}
          >
            {isLivestream ? "—" : "Canal"}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function BrandCard({
  brand,
  rank,
  expandTitle,
}: {
  brand: BrandRow;
  rank: number;
  expandTitle?: boolean;
}) {
  const imgUrl = brand.imageS3Key ? getS3Url(brand.imageS3Key) : "";
  const totalViews = brand.totalViews ?? 0;
  const showTotalViews = totalViews > 0;
  const viewsLabel = showTotalViews ? formatMetric(totalViews, "views") : null;
  return (
    <Box sx={expandTitle ? { ...rowBoxSx, ...rankingRowCaptureShellSx } : rowBoxSx}>
      <Typography
        sx={{
          fontFamily: '"DM Serif Text", serif',
          fontSize: 32,
          minWidth: 38,
          color: "#fff",
        }}
      >
        {rank}.
      </Typography>
      <Box sx={thumbSx}>
        {imgUrl ? (
          <Box
            component="img"
            src={imgUrl}
            alt=""
            sx={{
              width: "102%",
              height: "102%",
              objectFit: "cover",
              margin: "-1%",
              display: "block",
            }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontFamily: '"DM Serif Text", serif',
              fontSize: 32,
              lineHeight: "32px",
            }}
          >
            {brand.name.charAt(0).toUpperCase()}
          </Box>
        )}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          component="div"
          {...{ [RANKING_CAPTURE_FULL_TEXT_ATTR]: expandTitle ? "" : undefined }}
          sx={expandTitle ? rankingTitleCaptureSx : rankingTitleSx}
        >
          {brand.name}
        </Typography>
        <Typography
          sx={
            showTotalViews
              ? rankingMetricSx
              : {
                  fontFamily: '"DM Serif Text", serif',
                  fontSize: 20,
                  lineHeight: "20px",
                  color: "rgba(255,255,255,0.9)",
                }
          }
        >
          {showTotalViews ? viewsLabel : "—"}
        </Typography>
      </Box>
    </Box>
  );
}

const DEFAULT_FOOTER_RIGHT = "Detalles en radar.podscope.co";

/** Right-pointing arrow SVG (Figma 8805-2313) — inline so it works without asset server */
const ArrowRightSvg = () => (
  <svg
    width="22"
    height="28"
    viewBox="0 0 22 28"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: "block", marginBottom: "-8px" }}
  >
    <path
      d="M1 12h14M9 6l6 6-6 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Footer right block: "Más en Radar Podscope" + arrow (Figma 8750-847) — image/design only, not a link */
function FooterRightMoreLink() {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "10px",
        color: "#fff",
        mb: "2px",
      }}
    >
      <Typography
        sx={{
          fontFamily: '"DM Serif Text", serif',
          fontSize: 28,
          lineHeight: "28px",
          color: "#fff",
        }}
      >
        Más en radar.podscope.co
      </Typography>
    </Box>
  );
}

export const RadarPodscopeRankingCard = forwardRef<
  HTMLDivElement,
  RadarPodscopeRankingCardProps
>(function RadarPodscopeRankingCard(
  {
    programs = [],
    channels,
    brands,
    rankingPeriodLabel,
    subheader,
    footerExplainer,
    footerRight = DEFAULT_FOOTER_RIGHT,
    variant = "page",
    programRankingMetric,
    channelsMetric = "social-views",
    rankingParam,
    hidePeriodPill = false,
    livestreamTableNameColumnHeader = "Canal",
  },
  ref,
) {
  const isScreenshot = variant === "screenshot";
  const showLivestreamTable = Boolean(
    channels?.length && channelsMetric === "livestream-avg",
  );
  const leftItems = channels
    ? channels.slice(0, 5)
    : brands
      ? brands.slice(0, 5)
      : programs.slice(0, 5);
  const rightItems = channels
    ? channels.slice(5, 10)
    : brands
      ? brands.slice(5, 10)
      : programs.slice(5, 10);

  const renderRow = (
    item: ProgramRow | ChannelRow | BrandRow,
    rank: number,
  ) => {
    if (channels) {
      return (
        <ChannelCard
          key={`${(item as ChannelRow)._id}-${rank}`}
          channel={item as ChannelRow}
          rank={rank}
          channelsMetric={channelsMetric}
          expandTitle={isScreenshot}
        />
      );
    }
    if (brands) {
      return (
        <BrandCard
          key={`${(item as BrandRow)._id}-${rank}`}
          brand={item as BrandRow}
          rank={rank}
          expandTitle={isScreenshot}
        />
      );
    }
    return (
      <ProgramCard
        key={`${(item as ProgramRow)._id}-${rank}`}
        program={item as ProgramRow}
        rank={rank}
        metricType={programRankingMetric}
        hidePositionChange={isScreenshot}
      />
    );
  };

  return (
    <Box
      ref={ref}
      data-ranking-card
      sx={{
        width: RANKING_ASSET_WIDTH_PX,
        maxWidth: isScreenshot ? undefined : "100%",
        ...(isScreenshot
          ? { height: RANKING_ASSET_HEIGHT_PX }
          : {
              aspectRatio: `${RANKING_ASSET_WIDTH_PX}/${RANKING_ASSET_HEIGHT_PX}`,
            }),
        overflow: "hidden",
        bgcolor: "#1a1a1a",
        color: "#fff",
        pt: "96px",
        pb: "48px",
        px: "48px",
        position: "relative",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 0,
          "& img": {
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          },
        }}
      >
        <Box
          component="img"
          alt=""
          src={RANKING_CARD_BG_URL}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
        <Box
          sx={{ position: "absolute", inset: 0, bgcolor: "rgba(0,0,0,0.05)" }}
        />
      </Box>
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          flex: 1,
          minHeight: 0,
        }}
      >
        <RankingCardHeader subheader={subheader} />
        {!hidePeriodPill && (
          <Box
            sx={{ display: "flex", justifyContent: "center", flexShrink: 0 }}
          >
            <Box
              sx={{
                px: "18px",
                py: "8px",
                borderRadius: "30px",
                border: "2px solid #fff",
                bgcolor: "transparent",
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"DM Serif Text", serif',
                  fontSize: 24,
                  color: "#fff",
                }}
              >
                {rankingPeriodLabel}
              </Typography>
            </Box>
          </Box>
        )}
        {showLivestreamTable ? (
          <Box
            sx={{
              flexShrink: 1,
              minHeight: 0,
              overflow: isScreenshot ? "visible" : "auto",
              px: "72px",
              width: "100%",
            }}
          >
            <LivestreamRankingTable
              channels={channels!}
              nameColumnHeader={livestreamTableNameColumnHeader}
              forCapture={isScreenshot}
            />
          </Box>
        ) : (
          <Grid
            container
            columnSpacing={`${RANKING_ASSET_ROW_GAP_PX}px`}
            rowSpacing={`${RANKING_ASSET_ROW_GAP_PX}px`}
            sx={{
              flexShrink: 1,
              minHeight: 0,
              overflow: isScreenshot ? "visible" : "auto",
              px: "72px",
            }}
          >
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: `${RANKING_ASSET_ROW_GAP_PX}px`,
              }}
            >
              {leftItems.map((item, idx) => renderRow(item, idx + 1))}
            </Grid>
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: `${RANKING_ASSET_ROW_GAP_PX}px`,
              }}
            >
              {rightItems.map((item, idx) => renderRow(item, idx + 6))}
            </Grid>
          </Grid>
        )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            gap: 2,
            flexShrink: 0,
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "4px",
              flex: "1 1 auto",
              minWidth: 0,
              maxWidth: 600,
            }}
          >
            {rankingParam != null ? (
              <Typography
                component="div"
                sx={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: 16,
                  lineHeight: 1.35,
                  color: "#fff",
                  width: "100%",
                  maxWidth: FOOTER_EXPLAINER_MAX_WIDTH_PX,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontWeight: 600,
                    color: "#fff",
                    lineHeight: 1.15,
                    borderBottom: `4px solid ${getRankingDimensionFillForParam(rankingParam)}`,
                    pb: 0,
                    borderRadius: "0px",
                  }}
                >
                  Metodología
                </Box>
                <Box component="span" sx={{ fontWeight: 400 }}>
                  {`: ${footerExplainer}`}
                </Box>
              </Typography>
            ) : (
              <Typography
                sx={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: 14,
                  lineHeight: "12px",
                  color: "#fff",
                  width: "100%",
                  maxWidth: "100%",
                }}
              >
                {footerExplainer}
              </Typography>
            )}
          </Box>
          <FooterRightMoreLink />
        </Box>
      </Box>
    </Box>
  );
});
